// src/pages/TransactionHistory/TransactionHistory.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { transactionHistoryService } from '../../services/transactionHistoryService';
import './TransactionHistory.css';

const TransactionHistory = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('deposits');
  const [transactions, setTransactions] = useState({
    deposits: [],
    withdrawals: [],
    stockTrades: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch all transaction types using the service
      const [depositsData, withdrawalsData, allTransactionsData] = await Promise.all([
        transactionHistoryService.getDeposits(),
        transactionHistoryService.getWithdrawals(),
        transactionHistoryService.getUserTransactionHistory()
      ]);

      // Filter stock trades
      const allTransactions = allTransactionsData.results || allTransactionsData;
      const stockTrades = allTransactions.filter(
        t => t.transaction_type === 'buy_stock' || t.transaction_type === 'sell_stock'
      );

      // DEBUG: Log the raw stock trade data
      console.log('🔍 RAW STOCK TRADES DATA:', stockTrades);
      if (stockTrades.length > 0) {
        console.log('🔍 First stock trade total_trade_amount:', stockTrades[0].total_trade_amount);
        console.log('🔍 Type of total_trade_amount:', typeof stockTrades[0].total_trade_amount);
      }

      setTransactions({
        deposits: depositsData.results || depositsData,
        withdrawals: withdrawalsData.results || withdrawalsData,
        stockTrades: stockTrades
      });
    } catch (err) {
      setError('Failed to load transaction history');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (amount, method = '', transactionType = '') => {
    // Only crypto deposits use dollar sign, everything else uses naira
    const methodLower = method.toLowerCase();
    const isCryptoDeposit = methodLower.includes('crypto') && transactionType === 'deposit';
    const symbol = isCryptoDeposit ? '$' : '₦';
    return `${symbol}${Number(amount).toLocaleString()}`;
  };

  const formatStockCurrency = (amount) => {
    // Stock trades use naira sign
    console.log('💰 formatStockCurrency called with:', amount, 'Type:', typeof amount);
    const result = `₦${Number(amount).toLocaleString()}`;
    console.log('💰 formatStockCurrency returning:', result);
    return result;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: '#28a745',
      approved: '#28a745',
      pending: '#ffc107',
      processing: '#17a2b8',
      failed: '#dc3545',
      cancelled: '#6c757d'
    };
    return colors[status?.toLowerCase()] || '#6c757d';
  };

  const renderDepositHistory = () => (
    <div className="history-section">
      <div className="section-header">
        <span className="section-icon">⬇</span>
        <h2 className="section-title">Deposit History</h2>
      </div>

      {transactions.deposits.length === 0 ? (
        <div className="empty-state">
          <p>No deposit transactions found</p>
        </div>
      ) : (
        <div className="transactions-list">
          {transactions.deposits.map((transaction) => (
            <div key={transaction.id} className="transaction-card">
              <div className="transaction-main">
                <div className="transaction-amount">
                  {formatCurrency(transaction.display_amount || transaction.amount, transaction.method || '', 'deposit')}
                </div>
                <div
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(transaction.status) }}
                >
                  {transaction.status_display || transaction.status}
                </div>
              </div>
              <div className="transaction-details">
                <span className="transaction-date">
                  {formatDate(transaction.created_at)}
                </span>
                {transaction.method && (
                  <span className="transaction-method">
                    via {transaction.method.replace('_', ' ')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderWithdrawalHistory = () => (
    <div className="history-section">
      <div className="section-header">
        <span className="section-icon">⬆</span>
        <h2 className="section-title">Withdrawal History</h2>
      </div>

      {transactions.withdrawals.length === 0 ? (
        <div className="empty-state">
          <p>No withdrawal transactions found</p>
        </div>
      ) : (
        <div className="transactions-list">
          {transactions.withdrawals.map((transaction) => (
            <div key={transaction.id} className="transaction-card">
              <div className="transaction-main">
                <div className="transaction-amount">
                  {formatCurrency(transaction.display_amount || transaction.amount, transaction.method || '', 'withdrawal')}
                </div>
                <div
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(transaction.status) }}
                >
                  {transaction.status_display || transaction.status}
                </div>
              </div>
              <div className="transaction-details">
                <span className="transaction-date">
                  {formatDate(transaction.created_at)}
                </span>
                {transaction.method && (
                  <span className="transaction-method">
                    via {transaction.method.replace('_', ' ')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStockTradesHistory = () => (
    <div className="history-section">
      <div className="section-header">
        <span className="section-icon">📈</span>
        <h2 className="section-title">Stock Trading History</h2>
      </div>

      {transactions.stockTrades.length === 0 ? (
        <div className="empty-state">
          <p>No stock trading transactions found</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="trades-table">
            <thead>
              <tr>
                <th>Stock Name</th>
                <th>Shares</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.stockTrades.map((transaction) => (
                <tr key={transaction.id}>
                  <td>
                    <div className="stock-info">
                      <div className="stock-symbol">{transaction.stock_symbol}</div>
                      <div className="stock-name">{transaction.stock_name}</div>
                    </div>
                  </td>
                  <td>{transaction.shares_quantity}</td>
                  <td>{formatStockCurrency(transaction.total_trade_amount)}</td>
                  <td>
                    <div
                      className="trade-badge"
                      style={{
                        backgroundColor: transaction.transaction_type === 'buy_stock' ? '#28a745' : '#dc3545'
                      }}
                    >
                      {transaction.transaction_type === 'buy_stock' ? 'BUY' : 'SELL'}
                    </div>
                  </td>
                  <td>{formatDate(transaction.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="transaction-history-page">
      <div className="transaction-header">
        <button className="back-button" onClick={() => navigate('/portfolio')}>
          <span className="back-arrow">←</span> Back to Portfolio
        </button>
      </div>

      <div className="transaction-content">
        <div className="title-section">
          <h1 className="page-title">Transaction History</h1>
          <p className="page-subtitle">View all your deposits, withdrawals, and trading activity</p>
        </div>

        <div className="tabs-container">
          <button
            className={`tab ${activeTab === 'deposits' ? 'active' : ''}`}
            onClick={() => setActiveTab('deposits')}
          >
            <span className="tab-icon">⬇</span>
            <span>Funding</span>
          </button>
          <button
            className={`tab ${activeTab === 'withdrawals' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdrawals')}
          >
            <span className="tab-icon">⬆</span>
            <span>Withdrawals</span>
          </button>
          <button
            className={`tab ${activeTab === 'stockTrades' ? 'active' : ''}`}
            onClick={() => setActiveTab('stockTrades')}
          >
            <span className="tab-icon">📈</span>
            <span>Trades</span>
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading transactions...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-text">{error}</p>
            <button className="retry-button" onClick={fetchTransactions}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {activeTab === 'deposits' && renderDepositHistory()}
            {activeTab === 'withdrawals' && renderWithdrawalHistory()}
            {activeTab === 'stockTrades' && renderStockTradesHistory()}
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;